import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Col, message, Modal, Row, Typography, Button as AntButton} from 'antd';
import {ExclamationCircleOutlined, KeyOutlined} from '@ant-design/icons';
import {useAdminAuth} from '../../contexts/AdminAuthContext';
import ENDPOINTS from '../../constants/endpoints';
import {ROUTES, formatPath} from '../../constants/PathRoutes';
import api from '../../services/api';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import SwitchFormField from '../../components/data_entry/form/SwitchFormField';
import SelectFormField from '../../components/data_entry/form/SelectFormField';
import Button from '../../components/data_entry/Button';
import Loading from '../../components/admin/Loading';
import PasswordFields from '../../components/admin/PasswordFields';
import ChangePasswordModal from '../../components/admin/ChangePasswordModal';
import {BuildFieldErrorsDict} from '../../utils/utils';

const FORM_NAMES = {
    EMAIL: 'email',
    FIRST_NAME: 'first_name',
    LAST_NAME: 'last_name',
    PASSWORD: 'password',
    CONFIRM_PASSWORD: 'confirm_password',
    IS_ADMIN: 'is_admin',
    IS_ACTIVE: 'is_active',
    PERMISSIONS: 'permissions',
};

export default function AdminStaffForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEditing = !!id;
    const {user: currentUser, fetchMe} = useAdminAuth();
    const isEditingSelf = isEditing && currentUser?.profile?.id === Number(id);

    // Busca dados do membro (modo edição)
    const {data, isLoading: fetching} = useDefaultRequest({
        url: formatPath(ENDPOINTS.STAFF_DETAIL, {id}),
        makeRequest: isEditing,
        redirectRoute: ROUTES.ADMIN_STAFF_LIST
    });

    // Busca lista de permissões disponíveis
    const {data: permissionsList, isLoading: loadingPermissions} = useDefaultRequest({
        url: ENDPOINTS.STAFF_PERMISSIONS,
        dataDefault: []
    });

    // Popula o formulário quando os dados carregam (modo edição)
    useEffect(() => {
        if (data && isEditing) {
            form.setFieldsValue({
                [FORM_NAMES.EMAIL]: data[FORM_NAMES.EMAIL],
                [FORM_NAMES.FIRST_NAME]: data[FORM_NAMES.FIRST_NAME],
                [FORM_NAMES.LAST_NAME]: data[FORM_NAMES.LAST_NAME],
                [FORM_NAMES.IS_ADMIN]: data[FORM_NAMES.IS_ADMIN],
                [FORM_NAMES.IS_ACTIVE]: data[FORM_NAMES.IS_ACTIVE],
                [FORM_NAMES.PERMISSIONS]: data[FORM_NAMES.PERMISSIONS] || []
            });
            setIsAdmin(data[FORM_NAMES.IS_ADMIN]);
        }
    }, [data, isEditing, form]);

    // Quando is_admin muda, seleciona todas as permissões ou limpa
    const handleAdminChange = (checked) => {
        setIsAdmin(checked);
        if (checked) {
            const allPermissions = permissionsList.map(p => p.permission);
            form.setFieldsValue({[FORM_NAMES.PERMISSIONS]: allPermissions});
        }
    };

    const handleSave = async (values) => {
        setLoading(true);
        try {
            // Remove confirm_password do payload
            const {[FORM_NAMES.CONFIRM_PASSWORD]: confirm_password, ...payload} = values;

            // No modo edição, não envia a senha (usa o modal para isso)
            if (isEditing) {
                delete payload[FORM_NAMES.PASSWORD];
            }

            if (isEditing) {
                await api.patch(formatPath(ENDPOINTS.STAFF_DETAIL, {id}), payload);
                message.success('Membro atualizado com sucesso');

                // Se editou o próprio usuário, atualiza os dados do contexto
                if (isEditingSelf) {
                    await fetchMe();
                }
            } else {
                await api.post(ENDPOINTS.STAFF_LIST, payload);
                message.success('Membro criado com sucesso');
            }
            navigate(ROUTES.ADMIN_STAFF_LIST);
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async (values) => {
        // Se está editando o próprio usuário, mostra modal de confirmação
        if (isEditingSelf) {
            setPendingValues(values);
            setConfirmModalOpen(true);
            return;
        }

        await handleSave(values);
    };

    const handleConfirmSave = async () => {
        setConfirmModalOpen(false);
        if (pendingValues) {
            await handleSave(pendingValues);
            setPendingValues(null);
        }
    };

    const handleCancelConfirm = () => {
        setConfirmModalOpen(false);
        setPendingValues(null);
    };

    if (fetching || loadingPermissions) {
        return <Loading />;
    }

    const permissionOptions = permissionsList.map(p => ({
        label: p.name,
        value: p.permission,
        rawLabel: p.name
    }));

    return (
        <>
            <Row>
                <Col xs={24}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography.Title
                            style={{marginTop: 0}}
                            level={2}>
                            {isEditing ? 'Editar Membro' : 'Novo Membro'}
                        </Typography.Title>
                        {isEditing && (
                            <AntButton
                                type="default"
                                icon={<KeyOutlined/>}
                                onClick={() => setPasswordModalOpen(true)}
                            >
                                Alterar Senha
                            </AntButton>
                        )}
                    </div>
                </Col>
                <Col xs={24}>
                    <Form
                        form={form}
                        onFinish={onFinish}
                        initialValues={{[FORM_NAMES.IS_ADMIN]: false, [FORM_NAMES.IS_ACTIVE]: true, [FORM_NAMES.PERMISSIONS]: []}}
                    >
                        <Row gutter={[16, 0]}>
                            <Col xs={24} lg={12}>
                                <InputFormField
                                    placeholder="Digite o nome"
                                    form={{
                                        label: "Nome",
                                        name: FORM_NAMES.FIRST_NAME,
                                        rules: [{required: true, message: 'Informe o nome'}]
                                    }}
                                />
                            </Col>
                            <Col xs={24} lg={12}>
                                <InputFormField
                                    placeholder="Digite o sobrenome"
                                    form={{
                                        label: "Sobrenome",
                                        name: FORM_NAMES.LAST_NAME,
                                        rules: [{required: true, message: 'Informe o sobrenome'}]
                                    }}
                                />
                            </Col>
                            <Col xs={24}>
                                <InputFormField
                                    type="email"
                                    placeholder="Digite o e-mail"
                                    form={{
                                        label: "E-mail",
                                        name: FORM_NAMES.EMAIL,
                                        rules: [
                                            {required: true, message: 'Informe o e-mail'},
                                            {type: 'email', message: 'E-mail inválido'}
                                        ]
                                    }}
                                />
                            </Col>

                            {/* Campos de senha apenas no modo criação */}
                            {!isEditing && (
                                <Col xs={24}>
                                    <PasswordFields form={form} required={true}/>
                                </Col>
                            )}

                            <Col xs={24} lg={8}>
                                <SwitchFormField
                                    form={{
                                        label: "Administrador",
                                        name: FORM_NAMES.IS_ADMIN,
                                    }}
                                    onChange={handleAdminChange}
                                />
                            </Col>
                            <Col xs={24} lg={8}>
                                <SwitchFormField
                                    form={{
                                        label: "Ativo",
                                        name: FORM_NAMES.IS_ACTIVE,
                                    }}
                                />
                            </Col>

                            <Col xs={24}>
                                <SelectFormField
                                    mode="multiple"
                                    placeholder="Selecione as permissões"
                                    options={permissionOptions}
                                    disabled={isAdmin}
                                    form={{
                                        label: "Permissões",
                                        name: FORM_NAMES.PERMISSIONS,
                                        rules: [
                                            {
                                                validator: (_, value) => {
                                                    if (!isAdmin && (!value || value.length === 0)) {
                                                        return Promise.reject(new Error('Selecione pelo menos uma permissão'));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]
                                    }}
                                />
                                {isAdmin && (
                                    <Typography.Text type="secondary" style={{display: 'block', marginTop: -16, marginBottom: 16}}>
                                        Administradores possuem todas as permissões automaticamente.
                                    </Typography.Text>
                                )}
                            </Col>

                            <Col xs={24} style={{textAlign: 'right'}}>
                                <Button onClick={form.submit} loading={loading}>
                                    Salvar
                                </Button>
                                <Button
                                    type={'default'}
                                    style={{marginLeft: 8}}
                                    onClick={() => navigate(ROUTES.ADMIN_STAFF_LIST)}
                                >
                                    Cancelar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>

            {/* Modal de alteração de senha */}
            <ChangePasswordModal
                open={passwordModalOpen}
                onClose={() => setPasswordModalOpen(false)}
                staffId={id}
                staffName={data ? `${data.first_name} ${data.last_name}` : ''}
            />

            {/* Modal de confirmação para edição do próprio usuário */}
            <Modal
                title={
                    <span>
                        <ExclamationCircleOutlined style={{color: '#faad14', marginRight: 8}}/>
                        Confirmar alteração
                    </span>
                }
                open={confirmModalOpen}
                onOk={handleConfirmSave}
                onCancel={handleCancelConfirm}
                okText="Confirmar"
                cancelText="Cancelar"
                confirmLoading={loading}
            >
                <p>Você está alterando os dados do seu próprio usuário.</p>
                <p>Deseja continuar com a alteração?</p>
            </Modal>
        </>
    );
}